import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import type { SpotMistakeItem } from '../../services/mockQuizService';
import type { QuizProgress } from '../QuizBlock';

interface SpotMistakePreviewProps {
  items: SpotMistakeItem[];
  initialAnswers?: Record<number, { selected: string; correct: boolean }>;
  onAnswersChange?: (answers: Record<number, { selected: string; correct: boolean }>) => void;
  onProgressChange?: (progress: QuizProgress) => void;
}

export const SpotMistakePreview = ({ items, initialAnswers, onAnswersChange, onProgressChange }: SpotMistakePreviewProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, { selected: string; correct: boolean }>>(initialAnswers || {});

  const currentItem = items[currentIndex];
  const currentAnswer = answers[currentIndex];
  const showResult = currentAnswer !== undefined;
  const isCorrect = currentAnswer?.correct;

  // Calculate and report progress
  const updateProgress = useCallback(() => {
    const completed = Object.keys(answers).length;
    const correct = Object.values(answers).filter(a => a.correct).length;
    onProgressChange?.({ total: items.length, completed, correct });
  }, [answers, items.length, onProgressChange]);

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  const handleSubmit = (choice: string) => {
    const correct = choice === currentItem.answer;
    const newAnswers = {
      ...answers,
      [currentIndex]: { selected: choice, correct }
    };
    setAnswers(newAnswers);
    onAnswersChange?.(newAnswers);
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  if (!currentItem) return null;

  return (
    <div className="py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">
          Statement {currentIndex + 1} of {items.length}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex === items.length - 1}
            className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Incorrect statement */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <p className="text-xs text-red-600 font-medium mb-1">Find the mistake:</p>
        <p className="text-sm text-slate-700 italic">{currentItem.prompt}</p>
      </div>

      <p className="text-xs text-slate-500 mb-3">Select the correct version:</p>

      {/* Choices */}
      <div className="space-y-2 mb-4">
        {currentItem.choices.map((choice, index) => {
          const isSelected = currentAnswer?.selected === choice;
          const isCorrectChoice = choice === currentItem.answer;

          let bgColor = 'bg-white border-slate-200 hover:bg-slate-50 cursor-pointer';
          if (showResult) {
            if (isCorrectChoice) {
              bgColor = 'bg-emerald-50 border-emerald-300';
            } else if (isSelected && !isCorrect) {
              bgColor = 'bg-red-50 border-red-300';
            }
          } else if (isSelected) {
            bgColor = 'bg-slate-100 border-slate-400';
          }

          return (
            <button
              key={index}
              onClick={() => !showResult && handleSubmit(choice)}
              disabled={showResult}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors flex items-center gap-2 ${bgColor} ${showResult ? 'cursor-default' : ''}`}
            >
              <span className="flex-1">{choice}</span>
              {showResult && isCorrectChoice && <Check className="w-4 h-4 text-emerald-600" />}
              {showResult && isSelected && !isCorrect && <X className="w-4 h-4 text-red-600" />}
            </button>
          );
        })}
      </div>

      {/* Explanation */}
      {showResult && currentItem.explanation && (
        <div className={`p-3 rounded-lg text-sm ${isCorrect ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          <p className="font-medium mb-1">{isCorrect ? '✓ Correct!' : '✗ Incorrect'}</p>
          <p className="text-slate-600 text-xs">{currentItem.explanation}</p>
        </div>
      )}

      {/* Progress dots */}
      <div className="flex justify-center gap-1 mt-4">
        {items.map((_, idx) => {
          const answered = answers[idx] !== undefined;
          const wasCorrect = answers[idx]?.correct;
          return (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex
                  ? 'bg-slate-600 ring-2 ring-slate-300'
                  : answered
                  ? wasCorrect ? 'bg-emerald-400' : 'bg-red-400'
                  : 'bg-slate-200 hover:bg-slate-300'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};
